import { Input, Select, TextArea } from '@/components/DesignLibrary/Atoms';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import ExpressionEditor from '@/components/ExpressionEditor';
import Button from '@/components/DesignLibrary/Button';
import PlusCircle from '@components/Icons/PlusCircle';
import { useContext, useEffect, useMemo, useState } from 'react';
import CreateEditAiAgentModal from '@/modals/CreateEditAiAgentModal';
import getAiAgents from '@/tsClient/questionnaires/getAiAgents';
import useNoodleApi from '@/hooks/useNoodleApi';
import TeamsContext from '@/providers/Teams/TeamsContext';
import s from './AutofillEditor.module.scss';

type ThisAutofill =
  & Pick<QuestionnaireApiModels.FieldTemplateAutofill, 'description' | 'action' | 'populateFrom'>
  & {
    expression: Parameters<typeof ExpressionEditor>[0]['expression'] | null
  };

type AutofillEditorProps<A> = {
  autofill: A | null;
  fieldKey: string;
  onChange: (newAutofill: A) => void;
  questionnaireTemplateId: string;
};

const AutofillEditor = <A extends ThisAutofill>({
  autofill,
  fieldKey,
  onChange,
  questionnaireTemplateId,
}: AutofillEditorProps<A>): React.ReactElement => {
  const { creatorId } = useContext(TeamsContext);
  const [isAiAgentModalOpen, setIsAiAgentModalOpen] = useState(false);
  const [currentAiAgentId, setCurrentAiAgentId] = useState<string | null>(null);
  const [isCreate, setIsCreate] = useState(false);
  const { data: aiAgents, getData: getAiAgentsFn } = useNoodleApi(getAiAgents);
  const currentAutofill = autofill || {
    action: '',
    description: '',
    expression: null,
    populateFrom: '',
  };

  const aiAgentsParams = useMemo(() => {
    if (creatorId) {
      return {
        creatorId,
        page: 1,
        perPage: 100_000,
        questionnaireTemplateId,
      };
    }
    return null;
  }, [creatorId, questionnaireTemplateId]);

  useEffect(() => {
    const getAgents = async (): Promise<void> => {
      if (aiAgentsParams) {
        await getAiAgentsFn(aiAgentsParams);
      }
    };
    getAgents();
  }, [getAiAgentsFn, aiAgentsParams]);

  useEffect(() => {
    if (aiAgents && aiAgents.items.length > 0) {
      const matchingAgent = aiAgents.items.find((agent) => agent.fieldOutputs.includes(fieldKey));
      setCurrentAiAgentId(matchingAgent ? matchingAgent.id : null);
    }
  }, [aiAgents, fieldKey]);

  const setDescription = (newDescription: string): void => {
    onChange({
      ...currentAutofill,
      description: newDescription,
    } as A);
  };

  const setPopulateFrom = (populateFromText: string): void => {
    onChange({
      ...currentAutofill,
      populateFrom: populateFromText,
    } as A);
  };

  const setExpression = (newExpression: typeof currentAutofill.expression): void => {
    onChange({
      ...currentAutofill,
      expression: newExpression,
    } as A);
  };

  const setAction = (newAction: QuestionnaireApiModels.FieldTemplateAutofill['action']): void => {
    onChange({
      ...currentAutofill,
      action: newAction,
    } as A);
  };

  const clearAutofill = (): void => {
    if(onChange) {
      onChange(null as unknown as A);
    }
  };

  return (
    <>
      {isAiAgentModalOpen && (
        <CreateEditAiAgentModal
          aiAgent={isCreate ? null : (aiAgents?.items.find((agent) => agent.id === currentAiAgentId) ?? null)}
          onSave={async () => {
            setIsAiAgentModalOpen(false);
            setIsCreate(false);
            if (aiAgentsParams) {
              const updatedAgents = await getAiAgentsFn(aiAgentsParams);
              if (currentAiAgentId && (!updatedAgents?.data?.items || !updatedAgents.data.items.some(agent => agent.id === currentAiAgentId))) {
                setCurrentAiAgentId(null);
              }
            }
          }}
          onClose={() => {
            setIsCreate(false);
            setIsAiAgentModalOpen(false);
          }}
          questionnaireTemplateId={questionnaireTemplateId}
        />
      )}
      <Select
        label="Action"
        compact
        options={[
          { key: 'initial-values', label: 'Initial Values' },
          { key: 'get-filing-district', label: 'Get Filing District' },
          { key: 'conditional-fill', label: 'Conditional Fill' },
          { key: 'fill-from-dataset', label: 'Fill From Dataset' },
          { key: 'fill-from-internal-data', label: 'Fill From Internal Data' },
          { key: 'get-filing-county', label: 'Get Filing County' },
          { key: 'calculation', label: 'Calculation' },
          { key: 'ai-agent', label: 'AI Agent'},
        ]}
        value={currentAutofill.action || null}
        onChange={(value) => setAction(value as QuestionnaireApiModels.FieldTemplateAutofill['action'])}
        optional
      />
      {aiAgents && aiAgents.items.length > 0 && (
        <Select
          label="Select AI Agent to Edit"
          compact
          options={[
            { key: 'none', label: 'No Agent' },
            ...aiAgents.items.map((agent) => ({
              key: agent.id,
              label: agent.title,
            })),
          ]}
          value={currentAiAgentId || 'none'}
          onChange={(value) => setCurrentAiAgentId(value)}
          optional
        />
      )}
      <div className={s.agentButtons}>
        {currentAiAgentId && (
          <Button size='sm' variant='secondary' onClick={() => setIsAiAgentModalOpen(true)}>
            Edit Selected AI Agent
          </Button>
        )}
        <Button size='sm' variant='secondary'
          className={s.createAgent}
          onClick={() => {
            setIsAiAgentModalOpen(true);
            setIsCreate(true);
          }}>
          <>
            <PlusCircle size={12} weight="fill" />
            Create AI Agent
          </>
        </Button>
      </div>
      <Input label="" description="Populate from" value={currentAutofill.populateFrom} onChange={setPopulateFrom} optional />
      <TextArea
        optional
        label=''
        description='Description'
        onChange={setDescription}
        value={currentAutofill.description || null}
      />
      <ExpressionEditor
        fieldsInfo={{}}
        expression={currentAutofill.expression || null}
        canEditVars={true}
        onlyComplexEditor={true}
        onChange={setExpression}
        canEditFilterExpressionProperties
      />
      <div className={s.agentButtons}>
        <Button variant="destructive" size='sm' onClick={clearAutofill}>
          Clear Autofill
        </Button>
      </div>
    </>
  );
};

export default AutofillEditor;
